import React, { FunctionComponent } from "react";
import {
  BannerConsumer,
  BannerPlaceholder,
  BannerProvider,
  Boolean,
  Button,
  ColumnType,
  Form,
  IBannerContext,
  BannerStatus,
  TextInput
} from "@plex/react-components";
import { IViewController, useViewController } from "../ViewContext";
import { UserDefinedSchemaPropertiesGrid } from "./UserDefinedSchemaPropertiesGrid";
import { getDataTypeFromSchema, getSchema, listSchemas } from "../NodeTypes/DataSchemas";
import { openConfirmDialog } from "../Util/DialogUtil";
import { IDocumentObjectSchema } from "../FlowDocument/FlowDocumentModel";
import { forceNodeUpdate, refreshNodeDataPropertiesFromSchema } from "../Util/NodeUtil";
import { Edge, useReactFlow, useUpdateNodeInternals } from "reactflow";
import { variableInPropertyName, variableOutPropertyName } from "./LoopVariableEditor";
import { deleteEdgesFromSchemaProperty, getInputHandle, getOutputHandle } from "../Util/EdgeUtil";
import { DataType } from "../NodeTypes/TypeDefinitions";

export interface IUserDefinedSchemaFormProps {
  schemaId?: string;
  onCancel?: () => void;
  onSave?: (schemaId: string, schemaName: string) => void;
}

export const UserDefinedSchemaDetailsForm: FunctionComponent<IUserDefinedSchemaFormProps> = (props) => {
  const reactFlowInstance = useReactFlow();
  const updateNodeInternals = useUpdateNodeInternals();
  const { schemaId, onCancel, onSave } = props;
  const {
    userDefinedSchemaDetailsDialogState,
    setUserDefinedSchemaDetailsDialogState,
    userDefinedSchemaPropertyDetailsDialogState,
    setUserDefinedSchemaPropertyDetailsDialogState,
    userDefinedSchemaPropertyAddDialogState,
    setUserDefinedSchemaPropertyAddDialogState
  }: IViewController = useViewController();

  const onClose = () => {
    if (onCancel) {
      onCancel();
    } else {
      setUserDefinedSchemaDetailsDialogState({ ...userDefinedSchemaDetailsDialogState, show: false });
    }
  };

  const handleSave = (bctx: IBannerContext) => {
    // Don't modify name for loop variable schemas.
    if (userDefinedSchemaDetailsDialogState.loopVariable) {
      setUserDefinedSchemaDetailsDialogState({
        ...userDefinedSchemaDetailsDialogState,
        show: false
      });
      return;
    }

    const userDefinedSchemas = listSchemas({ userDefinedOnly: true, includeLists: false });
    if (
      userDefinedSchemas.some(
        (schema) => schema.name === userDefinedSchemaDetailsDialogState.schemaName && schema.id !== schemaId
      )
    ) {
      bctx.addMessage("A schema with this name already exists. Please choose a different name.", BannerStatus.warning);
      return;
    }
    if (onSave && schemaId) {
      onSave(schemaId, userDefinedSchemaDetailsDialogState.schemaName);
    }
    onClose();
  };

  return (
    <BannerProvider>
      <BannerConsumer>
        {(bctx: IBannerContext) => {
          return (
            <Form
              className="user-defined-schema-details-form"
              onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
                // Do not refresh the page when pressing enter!
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <Form.Header>
                <BannerPlaceholder style={{ marginTop: "20px", marginBottom: 0, marginLeft: 0, marginRight: 0 }} />
              </Form.Header>
              <Form.Body>
                {!userDefinedSchemaDetailsDialogState.loopVariable && (
                  <Form.Section title="Details" layout={ColumnType.single}>
                    <Form.Row className="user-defined-schema-details-form-input-row">
                      <Form.FieldPair labelText="Name" required={true}>
                        <TextInput
                          data-testid="user-defined-schema-name-input"
                          value={userDefinedSchemaDetailsDialogState.schemaName}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setUserDefinedSchemaDetailsDialogState({
                              ...userDefinedSchemaDetailsDialogState,
                              schemaName: e.target.value
                            })
                          }
                          placeholder="Enter schema name"
                        />
                      </Form.FieldPair>
                      <Form.FieldPair labelText="In Use" required={true}>
                        <Boolean
                          style={{ position: "relative", top: "7px" }}
                          value={userDefinedSchemaDetailsDialogState.schemaInUse}
                        />
                      </Form.FieldPair>
                    </Form.Row>
                  </Form.Section>
                )}
                <Form.Section title="Properties" layout={ColumnType.single}>
                  <Form.Row className="user-defined-schema-details-form-input-row">
                    <UserDefinedSchemaPropertiesGrid
                      schemaId={schemaId}
                      selectedPropertyName={userDefinedSchemaDetailsDialogState.selectedPropertyName}
                      onSelectionChanged={(propertyName) => {
                        setUserDefinedSchemaDetailsDialogState({
                          ...userDefinedSchemaDetailsDialogState,
                          selectedPropertyName: propertyName
                        });
                      }}
                    />
                  </Form.Row>
                </Form.Section>
              </Form.Body>
              <Form.Footer>
                <Form.Row className="user-defined-schema-details-form-button-row">
                  <Button
                    variance={"link"}
                    className="user-defined-schema-details-form-secondary-button"
                    as={"button"}
                    type="button"
                    onClick={onClose}
                  >
                    Cancel
                  </Button>
                  <Button
                    data-testid="user-defined-schema-add-property"
                    variance={"primary"}
                    className="user-defined-schema-details-form-secondary-button"
                    as={"button"}
                    type="button"
                    onClick={() =>
                      setUserDefinedSchemaPropertyAddDialogState({
                        ...userDefinedSchemaPropertyAddDialogState,
                        schemaId: schemaId ?? "",
                        show: true
                      })
                    }
                  >
                    Add
                  </Button>
                  <Button
                    data-testid="user-defined-schema-remove-property"
                    variance={"primary"}
                    className="user-defined-schema-details-form-secondary-button"
                    as={"button"}
                    type="button"
                    disabled={
                      !userDefinedSchemaDetailsDialogState.selectedPropertyName ||
                      userDefinedSchemaDetailsDialogState.schemaInUse
                    }
                    title={
                      userDefinedSchemaDetailsDialogState.schemaInUse
                        ? "Cannot delete property on schema that is being used"
                        : !userDefinedSchemaDetailsDialogState.selectedPropertyName
                        ? "Select a property"
                        : ""
                    }
                    onClick={() =>
                      openConfirmDialog("Are you sure you want to delete this property?", () => {
                        const schema = getSchema(schemaId!) as unknown as IDocumentObjectSchema;
                        const deletedPropertyDataType =
                          userDefinedSchemaDetailsDialogState.selectedPropertyName &&
                          schema.properties[userDefinedSchemaDetailsDialogState.selectedPropertyName] &&
                          (getDataTypeFromSchema(
                            schema.properties[userDefinedSchemaDetailsDialogState.selectedPropertyName]!.schema!
                          ) as DataType);
                        delete schema.properties[userDefinedSchemaDetailsDialogState.selectedPropertyName!];
                        refreshNodeDataPropertiesFromSchema(schema.id, reactFlowInstance, updateNodeInternals);
                        if (userDefinedSchemaDetailsDialogState.loopVariable) {
                          const loopNode = reactFlowInstance.getNode(userDefinedSchemaDetailsDialogState.loopNodeId!);
                          if (loopNode) {
                            const schemaHasProperties = Object.keys(schema.properties).length > 0;
                            const variablesIsEnabled =
                              loopNode.data.nodeProperties.inputs[variableInPropertyName].enabled &&
                              loopNode.data.nodeProperties.outputs[variableOutPropertyName].enabled;
                            if (schemaHasProperties != variablesIsEnabled) {
                              const variablesInputHasConnections = reactFlowInstance
                                .getEdges()
                                .some(
                                  (e: Edge) =>
                                    e.source === loopNode.id &&
                                    e.sourceHandle === getInputHandle(variableInPropertyName, DataType.OBJECT).toHandleId()
                                );
                              const variablesOutputHasConnections = reactFlowInstance
                                .getEdges()
                                .some(
                                  (e: Edge) =>
                                    e.target === loopNode.id &&
                                    e.targetHandle === getOutputHandle(variableOutPropertyName, DataType.OBJECT).toHandleId()
                                );
                              loopNode.data.nodeProperties.inputs[variableInPropertyName].enabled =
                                schemaHasProperties || variablesInputHasConnections;
                              loopNode.data.nodeProperties.outputs[variableOutPropertyName].enabled =
                                schemaHasProperties || variablesOutputHasConnections;
                              forceNodeUpdate(loopNode, updateNodeInternals, reactFlowInstance);
                            }
                            if (deletedPropertyDataType) {
                              deleteEdgesFromSchemaProperty(
                                schema.id,
                                userDefinedSchemaDetailsDialogState.selectedPropertyName!,
                                deletedPropertyDataType,
                                reactFlowInstance,
                                updateNodeInternals
                              );
                            }
                          }
                        }

                        setUserDefinedSchemaDetailsDialogState({
                          ...userDefinedSchemaDetailsDialogState,
                          selectedPropertyName: undefined
                        });
                      })
                    }
                  >
                    Delete
                  </Button>
                  <Button
                    data-testid="user-defined-schema-edit-property"
                    variance={"primary"}
                    className="user-defined-schema-details-form-secondary-button"
                    as={"button"}
                    type="button"
                    disabled={
                      !userDefinedSchemaDetailsDialogState.selectedPropertyName ||
                      userDefinedSchemaDetailsDialogState.schemaInUse
                    }
                    title={
                      userDefinedSchemaDetailsDialogState.schemaInUse
                        ? "Cannot edit property on schema that is being used"
                        : !userDefinedSchemaDetailsDialogState.selectedPropertyName
                        ? "Select a property"
                        : ""
                    }
                    onClick={() => {
                      setUserDefinedSchemaPropertyDetailsDialogState({
                        ...userDefinedSchemaPropertyDetailsDialogState,
                        show: true,
                        schemaId: schemaId!,
                        propertyName: userDefinedSchemaDetailsDialogState.selectedPropertyName!
                      });
                    }}
                  >
                    Edit
                  </Button>

                  <Button
                    data-testid="user-defined-schema-details-form-save"
                    variance={"primary"}
                    as={"button"}
                    type="button"
                    disabled={!userDefinedSchemaDetailsDialogState.schemaName.trim()}
                    onClick={() => handleSave(bctx)}
                  >
                    Ok
                  </Button>
                </Form.Row>
              </Form.Footer>
            </Form>
          );
        }}
      </BannerConsumer>
    </BannerProvider>
  );
};
